import { PROJECT_DATA_LOADED_IDLE_STATE } from '../hooks/loading-states';

/**
 * PSTUDIO-3897 — "Pro Studio only saves CSS on second attempt".
 *
 * `createChannel` / `updateChannel` are handled ONLY under the machine's `idle`
 * state. Dispatched anywhere else they bubble to `projectDataLoaded`, whose
 * handler calls `event.payload.errorCb?.()` and nothing else — the mutation is
 * silently thrown away.
 *
 * That is not a narrow race. Opening Channel Settings in update mode kicks off
 * three SERIALIZED loads (`loadPypeRuleGroups` → `loadApiCredentials` →
 * `loadLiveAgentPlatforms`), and the middle one fans out to one GraphQL request
 * per pype per env (`utils/get-pype-keys.ts`), so on a real project the machine
 * is out of `idle` for seconds after mount. Those loading flows are in
 * `NON_LOADING_STUDIO_STATES` on purpose (removing them blanks the page behind
 * the full-page <Loader>, which unmounts the form and re-fires the load chain in
 * a loop), so the page looks completely ready the whole time and Save is
 * enabled. A user who edits and saves in that window got no mutation, no error
 * and no navigation.
 *
 * So callers await this before dispatching, and the save simply lands a moment
 * later instead of vanishing.
 *
 * The state path is matched explicitly rather than asking `state.can({ type:
 * 'updateChannel' })`: `can()` reports `true` while the machine is busy, because
 * the bubble-up handler that DISCARDS the event is itself a handler.
 */

/** Interpreter surface this needs — kept structural so tests can pass a stub. */
export interface IdleWaitableService {
  state: { matches: (stateValue: string) => boolean };
  subscribe: (
    listener: (state: { matches: (s: string) => boolean }) => void
  ) => {
    unsubscribe: () => void;
  };
}

export class ProStudioBusyTimeoutError extends Error {
  constructor() {
    super('pro-studio-busy-timeout');
    this.name = 'ProStudioBusyTimeoutError';
  }
}

export class ProStudioUnavailableError extends Error {
  constructor(state: string) {
    super(`pro-studio-unavailable:${state}`);
    this.name = 'ProStudioUnavailableError';
  }
}

/**
 * States the machine parks in and does not leave on its own, so waiting for
 * `idle` from one of them can only ever end in the timeout. Failing immediately
 * turns a 30-second dead wait into an instant, honest error.
 *
 * `botFlowError` is deliberately NOT here: it returns to `idle` after 100ms
 * (`after: { 100: '#idle' }`), so a save that lands during it should wait, not
 * fail.
 */
const TERMINAL_STATES = ['accessDenied', 'error', 'solutionNotFound'] as const;

const findTerminalState = (state: {
  matches: (value: string) => boolean;
}): string | undefined => TERMINAL_STATES.find((value) => state.matches(value));

/**
 * Generous by design: the wait only ever runs while a load chain is genuinely in
 * flight, and timing out means losing the user's save — the thing this exists to
 * prevent. It is a backstop against a wedged machine, not a latency budget.
 */
export const DEFAULT_IDLE_TIMEOUT_MS = 30_000;

/**
 * Resolves once the machine can actually accept a channel mutation. Rejects with
 * `ProStudioBusyTimeoutError` if that never happens, so the caller can surface a
 * real error instead of hanging a "Saving…" spinner forever.
 */
export const waitForProStudioIdle = (
  service: IdleWaitableService,
  timeoutMs: number = DEFAULT_IDLE_TIMEOUT_MS
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (service.state.matches(PROJECT_DATA_LOADED_IDLE_STATE)) {
      resolve();
      return;
    }

    const terminalOnEntry = findTerminalState(service.state);
    if (terminalOnEntry) {
      reject(new ProStudioUnavailableError(terminalOnEntry));
      return;
    }

    // `subscribe` replays the current state SYNCHRONOUSLY, so the listener can
    // run before the subscription handle exists. `teardown` starts as a no-op and
    // is swapped in once both the timer and the handle are held, which keeps that
    // first call safe; the trailing `settled` check then cleans up for it.
    let settled = false;
    let teardown = () => {};

    const finish = (onDone: () => void) => {
      if (settled) return;
      settled = true;
      teardown();
      onDone();
    };

    const timer = setTimeout(
      () => finish(() => reject(new ProStudioBusyTimeoutError())),
      timeoutMs
    );

    const subscription = service.subscribe((state) => {
      if (state.matches(PROJECT_DATA_LOADED_IDLE_STATE)) {
        finish(resolve);
        return;
      }
      const terminal = findTerminalState(state);
      if (terminal) {
        finish(() => reject(new ProStudioUnavailableError(terminal)));
      }
    });

    const cleanUp = () => {
      clearTimeout(timer);
      subscription.unsubscribe();
    };
    teardown = cleanUp;

    if (settled) {
      cleanUp();
    }
  });
